
import React, { useState, useEffect } from 'react';
import { MacroBeltTimeline } from './MacroBeltTimeline';
import { MesoBeltTimeline } from './MesoBeltTimeline';
import { MicroBeltTimeline } from './MicroBeltTimeline';
import { ZoomControls } from './ZoomControls';
import { MacroCurriculum, MesoCurriculum } from '@/services/roadmap/roadmapService';

import { useMicroBeltScroll } from './useMicroBeltScroll';
import { trainingJournalCircuitBreaker } from '@/utils/queryCircuitBreaker';

interface ZoomableTimelineProps {
  macroData: MacroCurriculum[];
  mesoData: MesoCurriculum[];
}

export const ZoomableTimeline: React.FC<ZoomableTimelineProps> = ({
  macroData,
  mesoData,
}) => {
  const [zoomLevel, setZoomLevel] = useState<'macro' | 'meso' | 'micro'>('macro');
  const [error, setError] = useState<string | null>(null);

  const { scrollContainerRef } = useMicroBeltScroll();

  // Clear errors when zoom level changes
  useEffect(() => {
    setError(null);
  }, [zoomLevel]);

  const handleZoomChange = (newZoom: 'macro' | 'meso' | 'micro') => {
    try {
      // Check circuit breaker state before allowing micro zoom
      if (newZoom === 'micro' && trainingJournalCircuitBreaker.isOpen()) {
        setError('Timeline temporarily unavailable due to system issues. Please try again later.');
        return;
      }
      
      setZoomLevel(newZoom);
      setError(null);
    } catch (err) {
      console.error('Error changing zoom level:', err);
      setError('Failed to change timeline view');
    }
  };

  const handleSliderChange = (value: number) => {
    const levels: ('macro' | 'meso' | 'micro')[] = ['macro', 'meso', 'micro'];
    const newLevel = levels[value];
    if (newLevel) {
      handleZoomChange(newLevel);
    }
  };

  const handleZoomIn = () => {
    if (zoomLevel === 'macro') {
      handleZoomChange('meso');
    } else if (zoomLevel === 'meso') {
      handleZoomChange('micro');
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel === 'micro') {
      handleZoomChange('meso');
    } else if (zoomLevel === 'meso') {
      handleZoomChange('macro');
    }
  };

  const renderTimeline = () => {
    try {
      switch (zoomLevel) {
        case 'macro':
          return (
            <MacroBeltTimeline
              data={macroData}
            />
          );
        case 'meso':
          return (
            <MesoBeltTimeline
              data={mesoData}
            />
          );
        case 'micro':
          if (trainingJournalCircuitBreaker.isOpen()) {
            return (
              <div className="h-16 bg-muted rounded-lg relative overflow-hidden flex items-center justify-center">
                <div className="text-sm text-muted-foreground">Micro timeline temporarily unavailable</div>
              </div>
            );
          }
          return (
            <MicroBeltTimeline
              data={mesoData}
            />
          );
        default:
          return null;
      }
    } catch (err) {
      console.error('Error rendering timeline:', err);
      setError('Failed to render timeline');
      return (
        <div className="h-16 bg-muted rounded-lg relative overflow-hidden flex items-center justify-center">
          <div className="text-sm text-destructive">Timeline error</div>
        </div>
      );
    }
  };

  return (
    <div className="space-y-4" ref={scrollContainerRef}>
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Your Journey</h3>
        <ZoomControls 
          currentLevel={zoomLevel} 
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          canZoomIn={zoomLevel !== 'micro'}
          canZoomOut={zoomLevel !== 'macro'}
          sliderValue={zoomLevel === 'macro' ? 0 : zoomLevel === 'meso' ? 1 : 2}
          onSliderChange={handleSliderChange}
        />
      </div>
      
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      
      <div className="relative bg-card border rounded-lg p-4">
        {renderTimeline()}
      </div>
    </div>
  );
};
